'use client';

import { isAfter, isBefore } from 'date-fns';
import type { Session } from 'next-auth';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import ActionButton from '../../../../components/form/ActionButton/ActionButton';
import utilsStyles from '../../../../styles/modules/utils.module.scss';
import { diffDateTimeHours, newDate } from '../../../../utils';
import styles from './SerialCodeFab.module.scss';

interface SerialCodeFabUIProps {
  startAt: string;
  endAt: string;
  session?: Session | null;
  customOptions?: {
    changeCampaignLotteryThesaurus?: string;
  };
  showSerialCodeNote?: boolean;
  onOpenSerialCodeModal: () => void;
  lotteryStatus: {
    is_session: { value: string };
    later: { value: string };
    upcoming: { value: string };
  };
}

export function SerialCodeFab({
  startAt,
  endAt,
  session = null,
  customOptions,
  showSerialCodeNote = false,
  onOpenSerialCodeModal,
  lotteryStatus,
}: SerialCodeFabUIProps) {
  const router = useRouter();
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const endAtDate = newDate(endAt);
    const interval = window.setInterval(() => {
      const currentDate = newDate();
      const diff = diffDateTimeHours(endAtDate, currentDate);
      setTimeLeft(`${diff.days}日${diff.hours}時間${diff.minutes}分${diff.seconds}秒`);
    }, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, [endAt]);

  const isPeriodLottery = (startAt: string, endAt: string) => {
    return endAt && nowLotteryStatus(startAt, endAt) === lotteryStatus.is_session.value;
  };

  const openSerialCodeModal = () => {
    onOpenSerialCodeModal();
  };

  const goSignIn = () => {
    const params = new URLSearchParams(location.search);
    params.set('callbackUrl', location.pathname);
    router.push(`/auth/signin?${params.toString()}`);
  };

  return (
    <div className={styles.serialCodeFab}>
      <div className={styles.container}>
        <div className={styles.drawText}>
          <p className={`${styles.challengeText} ${utilsStyles.textBold}`}>
            <span>シリアルコードを入力して</span>
            <br />
            {customOptions?.changeCampaignLotteryThesaurus && (
              <span>{customOptions.changeCampaignLotteryThesaurus}</span>
            )}
            <span>キャンペーンくじにチャレンジ</span>
          </p>
          {isPeriodLottery(startAt, endAt) && <p className={styles.timeLeft}>終了まで{timeLeft}</p>}
        </div>
        {session === null ? (
          <div className={styles.drawButton}>
            <ActionButton
              onClick={goSignIn}
              color='primary'
              disabled={nowLotteryStatus(startAt, endAt) !== lotteryStatus.is_session.value}
              dataTestId='login-to-draw-serial'
            >
              {/* 未ログイン */}
              <div className={utilsStyles.centeredVertical}>
                <p>ログインして</p>
                <p>くじを引く</p>
              </div>
            </ActionButton>
          </div>
        ) : (
          <div className={styles.drawButton}>
            <ActionButton
              onClick={openSerialCodeModal}
              color='primary'
              disabled={nowLotteryStatus(startAt, endAt) !== lotteryStatus.is_session.value}
              dataTestId='open-serial-code-input-modal-button'
            >
              {/* 通常（引ける状態） */}
              <div className={utilsStyles.centeredVertical}>
                <p>くじを引く</p>
                {showSerialCodeNote && (
                  <p className={styles.textSmall}>※くじを引くごとにシリアルコードの入力が必要です</p>
                )}
              </div>
            </ActionButton>
          </div>
        )}
      </div>
    </div>
  );
}

function nowLotteryStatus(startAt: string, endAt: string) {
  if (endAt && isAfter(newDate(), newDate(endAt))) {
    return 'later';
  }
  if (isBefore(newDate(), newDate(startAt))) {
    return 'upcoming';
  }
  return 'is_session';
}
